import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import moment from 'moment';
// @mui
import {
  Card,
  Table,
  Stack,
  Button,
  IconButton,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  TableHead,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
// components
import Scrollbar from '../components/scrollbar';
// sections
import { UserListHead, UserListToolbar } from '../sections/@dashboard/user';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Iconify from 'src/components/iconify';
import hit from 'src/services/manager';
import { setLoader } from 'src/redux/loader';
import { setToast } from 'src/redux/toast';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'name', label: 'Name', alignRight: false },
  { id: 'email', label: 'Email', alignRight: false },
  { id: 'phone', label: 'Phone', alignRight: false },
  { id: 'accessCode', label: 'Access Code', alignRight: false },
  { id: 'createdAt', label: 'Created', alignRight: false },
  { id: '_id', label: 'Partner ID', alignRight: false },
  { id: 'actions', label: 'Actions', alignRight: true },
];

const getPartnerName = (partner) =>
  partner?.name || partner?.companyName || partner?.businessName || partner?.title || '';

const getPartnerValue = (partner, key) => {
  switch (key) {
    case 'name':
      return getPartnerName(partner);
    case 'email':
      return partner?.email || partner?.contactEmail || '';
    case 'phone':
      return partner?.phone || partner?.contactPhone || '';
    case 'accessCode':
      return partner?.accessCode || partner?.access_code || '';
    case 'createdAt':
      return partner?.createdAt || partner?.created_at || '';
    case '_id':
      return partner?._id || partner?.id || '';
    default:
      return partner?.[key] || '';
  }
};

function descendingComparator(a, b, orderBy) {
  const aValue = getPartnerValue(a, orderBy);
  const bValue = getPartnerValue(b, orderBy);
  if (bValue < aValue) {
    return -1;
  }
  if (bValue > aValue) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    const q = query.toLowerCase();
    return stabilizedThis
      .map((el) => el[0])
      .filter((partner) => {
        const name = getPartnerName(partner).toLowerCase();
        const email = (partner?.email || partner?.contactEmail || '').toLowerCase();
        return name.includes(q) || email.includes(q);
      });
  }
  return stabilizedThis.map((el) => el[0]);
}

export default function PartnersPage() {
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [partners, setPartners] = useState([]);
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    notes: '',
    partnerPic: '',
    userName: '',
    userEmail: '',
  });
  const [editFormData, setEditFormData] = useState({
    name: '',
    contactEmail: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    notes: '',
    partnerPic: '',
    accessCode: '',
  });
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const generatePassword = (length = 12) => {
    const letters = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const digits = '0123456789';
    const all = `${letters}${digits}`;
    const pick = (chars) => chars[Math.floor(Math.random() * chars.length)];
    const base = [pick(letters), pick(digits)];
    for (let i = base.length; i < length; i += 1) {
      base.push(pick(all));
    }
    for (let i = base.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [base[i], base[j]] = [base[j], base[i]];
    }
    return base.join('');
  };

  const resetForm = () => {
    setFormData({
      name: '',
      street: '',
      city: '',
      state: '',
      zip: '',
      phone: '',
      notes: '',
      partnerPic: '',
      userName: '',
      userEmail: '',
    });
    setGeneratedPassword(generatePassword());
  };

  const fetchPartners = async () => {
    try {
      dispatch(setLoader(true));
      const res = await hit('/admin/partners', 'get', { limit: 100 });
      if (!res.err) {
        const list =
          res.data?.results || res.data?.data || res.data?.partners || res.data || [];
        setPartners(Array.isArray(list) ? list : []);
      } else {
        dispatch(setToast({ show: true, message: res.msg, severity: 'error' }));
      }
    } catch (err) {
      dispatch(setToast({ show: true, message: 'Failed to load partners.', severity: 'error' }));
    } finally {
      dispatch(setLoader(false));
    }
  };

  useEffect(() => {
    fetchPartners();
  }, []);
  const openAddDialog = () => {
    resetForm();
    setOpenAdd(true);
  };

  const closeAddDialog = () => {
    setOpenAdd(false);
  };

  const updateForm = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const updateEditForm = (field) => (event) => {
    setEditFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const openEditDialog = (partner) => {
    setSelectedPartner(partner);
    setEditFormData({
      name: getPartnerName(partner),
      contactEmail: partner?.contactEmail || partner?.email || '',
      street: partner?.street || '',
      city: partner?.city || '',
      state: partner?.state || '',
      zip: partner?.zip || '',
      phone: partner?.phone || partner?.contactPhone || '',
      notes: partner?.notes || '',
      partnerPic: partner?.partnerPic || '',
      accessCode: partner?.accessCode || partner?.access_code || '',
    });
    setOpenEdit(true);
  };

  const closeEditDialog = () => {
    setOpenEdit(false);
    setSelectedPartner(null);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const submitNewPartner = async () => {
    if (!formData.name.trim()) {
      dispatch(setToast({ show: true, message: 'Partner name is required.', severity: 'error' }));
      return;
    }
    if (!formData.userName.trim() || !formData.userEmail.trim()) {
      dispatch(setToast({ show: true, message: 'Partner user name and email are required.', severity: 'error' }));
      return;
    }
    try {
      dispatch(setLoader(true));
      const partnerPayload = {
        name: formData.name.trim(),
        street: formData.street.trim(),
        city: formData.city.trim(),
        state: formData.state.trim(),
        zip: formData.zip.trim(),
        phone: formData.phone.trim(),
        notes: formData.notes.trim(),
      };
      if (formData.partnerPic.trim()) {
        partnerPayload.partnerPic = formData.partnerPic.trim();
      }
      const partnerRes = await hit('/admin/partners', 'post', partnerPayload);
      if (partnerRes.err) {
        dispatch(setToast({ show: true, message: partnerRes.msg, severity: 'error' }));
        return;
      }
      const partnerId = partnerRes.data?._id || partnerRes.data?.id;
      if (!partnerId) {
        dispatch(setToast({ show: true, message: 'Partner created but ID missing.', severity: 'error' }));
        return;
      }
      const userRes = await hit(`/admin/partners/${partnerId}/users`, 'post', {
        name: formData.userName.trim(),
        email: formData.userEmail.trim(),
        password: generatedPassword,
        partner_id: partnerId,
      });
      if (userRes.err) {
        dispatch(setToast({ show: true, message: userRes.msg, severity: 'error' }));
        return;
      }
      dispatch(setToast({ show: true, message: 'Partner created successfully.', severity: 'success' }));
      closeAddDialog();
      fetchPartners();
    } catch (err) {
      dispatch(setToast({ show: true, message: 'Failed to create partner.', severity: 'error' }));
    } finally {
      dispatch(setLoader(false));
    }
  };

  const submitEditPartner = async () => {
    const partnerId = selectedPartner?._id || selectedPartner?.id;
    if (!partnerId) {
      dispatch(setToast({ show: true, message: 'Partner ID is missing.', severity: 'error' }));
      return;
    }
    if (!editFormData.name.trim()) {
      dispatch(setToast({ show: true, message: 'Partner name is required.', severity: 'error' }));
      return;
    }

    const normalizedCurrentCode = (selectedPartner?.accessCode || selectedPartner?.access_code || '').trim().toUpperCase();
    const normalizedUpdatedCode = editFormData.accessCode.trim().toUpperCase();
    const shouldUpdateAccessCode = normalizedUpdatedCode && normalizedUpdatedCode !== normalizedCurrentCode;

    try {
      dispatch(setLoader(true));

      const partnerPayload = {
        name: editFormData.name.trim(),
        street: editFormData.street.trim(),
        city: editFormData.city.trim(),
        state: editFormData.state.trim(),
        zip: editFormData.zip.trim(),
        phone: editFormData.phone.trim(),
        notes: editFormData.notes.trim(),
      };

      if (editFormData.partnerPic.trim()) {
        partnerPayload.partnerPic = editFormData.partnerPic.trim();
      }

      if (editFormData.contactEmail.trim()) {
        partnerPayload.contactEmail = editFormData.contactEmail.trim();
      }

      const partnerRes = await hit(`/admin/partners/${partnerId}`, 'patch', partnerPayload);
      if (partnerRes.err) {
        dispatch(setToast({ show: true, message: partnerRes.msg || 'Failed to update partner.', severity: 'error' }));
        return;
      }

      if (shouldUpdateAccessCode) {
        const accessCodeRes = await hit(`/admin/partners/${partnerId}/access-code`, 'patch', {
          code: normalizedUpdatedCode,
        });
        if (accessCodeRes.err) {
          dispatch(
            setToast({
              show: true,
              message: accessCodeRes.msg || 'Partner updated, but failed to update access code.',
              severity: 'error',
            })
          );
          return;
        }
      }

      dispatch(setToast({ show: true, message: 'Partner updated successfully.', severity: 'success' }));
      closeEditDialog();
      fetchPartners();
    } catch (err) {
      dispatch(setToast({ show: true, message: 'Failed to update partner.', severity: 'error' }));
    } finally {
      dispatch(setLoader(false));
    }
  };

  const filteredPartners = applySortFilter(partners, getComparator(order, orderBy), filterName);
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredPartners.length) : 0;
  const isNotFound = !filteredPartners.length && !!filterName;

  return (
    <>
      <Helmet>
        <title> Partners | SuperSenses UI </title>
      </Helmet>

      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h4" gutterBottom>
            Partners
          </Typography>
          <Button variant="contained" onClick={openAddDialog}>
            Add Partner
          </Button>
        </Stack>

        <Card>
          <UserListToolbar
            numSelected={0}
            filterName={filterName}
            onFilterName={handleFilterByName}
            showInput
            headerText="Partners"
            placeholder="Search partners..."
          />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <UserListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={partners.length}
                  numSelected={0}
                  onRequestSort={handleRequestSort}
                />
                <TableBody>
                  {filteredPartners
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => {
                      const name = getPartnerName(row);
                      const email = row?.email || row?.contactEmail || '—';
                      const phone = row?.phone || row?.contactPhone || '—';
                      const accessCode = row?.accessCode || row?.access_code || '—';
                      const createdAt = row?.createdAt || row?.created_at;
                      const partnerId = row?._id || row?.id || '—';

                      return (
                        <TableRow
                          hover
                          key={partnerId || index}
                          tabIndex={-1}
                          role="checkbox"
                          onClick={() =>
                            navigate(`/dashboard/partners/${partnerId}`, {
                              state: { partnerName: name || partnerId },
                            })
                          }
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell padding="checkbox">{page * rowsPerPage + index + 1}</TableCell>
                          <TableCell component="th" scope="row" padding="none">
                            <Typography>{name || '—'}</Typography>
                          </TableCell>
                          <TableCell align="left">{email}</TableCell>
                          <TableCell align="left">{phone}</TableCell>
                          <TableCell align="left">{accessCode}</TableCell>
                          <TableCell align="left">
                            {createdAt ? moment(createdAt).format('MM/DD/YYYY hh:mm a') : '—'}
                          </TableCell>
                          <TableCell align="left">{partnerId}</TableCell>
                          <TableCell align="right">
                            <IconButton
                              onClick={(event) => {
                                event.stopPropagation();
                                openEditDialog(row);
                              }}
                              aria-label="Edit partner"
                            >
                              <Iconify icon="eva:edit-fill" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}

                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={8} />
                    </TableRow>
                  )}
                </TableBody>

                {isNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={8} sx={{ py: 3 }}>
                        <Typography variant="body2">No partners found.</Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredPartners.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>

      </Container>

      <Dialog open={openAdd} onClose={closeAddDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add Partner</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <Typography variant="subtitle1">Partner Details</Typography>
            <TextField label="Partner Name" value={formData.name} onChange={updateForm('name')} required />
            <TextField label="Street" value={formData.street} onChange={updateForm('street')} />
            <Stack direction="row" spacing={2}>
              <TextField label="City" value={formData.city} onChange={updateForm('city')} fullWidth />
              <TextField label="State" value={formData.state} onChange={updateForm('state')} fullWidth />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Zip" value={formData.zip} onChange={updateForm('zip')} fullWidth />
              <TextField label="Phone" value={formData.phone} onChange={updateForm('phone')} fullWidth />
            </Stack>
            <TextField
              label="Partner Picture Path"
              value={formData.partnerPic}
              onChange={updateForm('partnerPic')}
              placeholder="public/user.png"
            />
            <TextField
              label="Notes"
              value={formData.notes}
              onChange={updateForm('notes')}
              multiline
              minRows={3}
            />

            <Typography variant="subtitle1">Partner User</Typography>
            <TextField label="User Name" value={formData.userName} onChange={updateForm('userName')} required />
            <TextField
              label="User Email"
              value={formData.userEmail}
              onChange={updateForm('userEmail')}
              type="email"
              required
            />
            <TextField label="Generated Password" value={generatedPassword} InputProps={{ readOnly: true }} />
            <Button
              variant="outlined"
              onClick={() => setGeneratedPassword(generatePassword())}
              sx={{ alignSelf: 'flex-start' }}
            >
              Regenerate Password
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAddDialog}>Cancel</Button>
          <Button variant="contained" onClick={submitNewPartner}>
            Create Partner
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openEdit} onClose={closeEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Partner</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <Typography variant="subtitle1">Partner Details</Typography>
            <TextField
              label="Partner Name"
              value={editFormData.name}
              onChange={updateEditForm('name')}
              required
              fullWidth
            />
            <TextField
              label="Contact Email"
              type="email"
              value={editFormData.contactEmail}
              onChange={updateEditForm('contactEmail')}
              fullWidth
            />
            <Stack direction="row" spacing={2}>
              <TextField label="Street" value={editFormData.street} onChange={updateEditForm('street')} fullWidth />
              <TextField label="Phone" value={editFormData.phone} onChange={updateEditForm('phone')} fullWidth />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="City" value={editFormData.city} onChange={updateEditForm('city')} fullWidth />
              <TextField label="State" value={editFormData.state} onChange={updateEditForm('state')} fullWidth />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Zip" value={editFormData.zip} onChange={updateEditForm('zip')} fullWidth />
              <TextField
                label="Access Code"
                value={editFormData.accessCode}
                onChange={updateEditForm('accessCode')}
                fullWidth
                helperText="4-32 chars. Saved uppercase by server."
              />
            </Stack>
            <TextField
              label="Partner Picture Path"
              value={editFormData.partnerPic}
              onChange={updateEditForm('partnerPic')}
              placeholder="public/user.png"
              fullWidth
            />
            <TextField
              label="Notes"
              value={editFormData.notes}
              onChange={updateEditForm('notes')}
              multiline
              minRows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditDialog}>Cancel</Button>
          <Button variant="contained" onClick={submitEditPartner}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
